// Global
import React from 'react';
import type {
  ActionProp,
  ItemClickedAction,
  SearchResultsInitialState,
  SearchResultsStoreState,
} from '@sitecore-search/react';
import { WidgetDataType, useSearchResultsActions, widget } from '@sitecore-search/react';
import { ArticleCard, Pagination } from '@sitecore-search/ui';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';

// Lib
import { BoKNavigation } from 'lib/templates/Feature.BOKF.model';
import { useSanitizedSearchResults } from 'lib/utils/token-replacer';
import {
  ArticleModel,
  QueryResultsSummaryProps,
  SearchPaginationProps,
} from 'types/Search/Search.Models';

// Local
import SVG from 'helpers/SVG/SVG';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import { Spinner } from 'components/search';
import { useRealPathName } from 'lib/utils/useRealPathname';

export type SearchProps = BoKNavigation.SiteSearch;

type CategoryKey =
  | 'recommended-category-1'
  | 'recommended-category-2'
  | 'recommended-category-3'
  | 'recommended-category-4'
  | 'recommended-category-5'
  | 'recommended-category-6';

const categoryStylesMap = {
  'recommended-category-1': {
    bg: 'bg-light-other-redVibrant3/60',
    text: 'text-light-other-redVibrant2',
  },
  'recommended-category-2': {
    bg: 'bg-light-other-orangeWarm3/60',
    text: 'text-light-other-orangeWarm2',
  },
  'recommended-category-3': {
    bg: 'bg-light-other-plum3/60',
    text: 'text-light-other-plum2',
  },
  'recommended-category-4': {
    bg: 'bg-light-other-burgundy3/40',
    text: 'text-light-other-burgundy2',
  },
  'recommended-category-5': {
    bg: 'bg-light-other-red/50',
    text: 'text-light-fill-tertiary-brand',
  },
  'recommended-category-6': {
    bg: 'bg-light-other-violet3/60',
    text: 'text-light-other-violet2',
  },
};

const getCategoryStyles = (categoryColor: string | undefined) => {
  return (
    categoryStylesMap[categoryColor as CategoryKey] || categoryStylesMap['recommended-category-6']
  );
};
type ArticleCardItemCardProps = {
  className?: string;
  displayText?: boolean;
  article: ArticleModel;
  onItemClick: ActionProp<ItemClickedAction>;
  index: number;
};

const ArticleHorizontalItemCard = ({
  className = '',
  article,
  onItemClick,
  index,
}: ArticleCardItemCardProps) => {
  const categoryStyles = getCategoryStyles(article.pagecategorycolor);
  return (
    <ArticleCard.Root key={article.id} className={`flex flex-col w-full ${className}`}>
      <a
        className="flex flex-col gap-y-2 p-4 -ml-4 w-full border-b border-light-stroke-primary hover:no-underline hover:bg-light-background-neutral-low focus:outline-2 focus:outline focus:outline-light-stroke-primary-variant-highest focus:-outline-offset-2 focus:no-underline"
        href={article.url}
        onClick={() => {
          onItemClick({
            id: article.id,
            index,
            sourceId: article.source_id,
          });
        }}
      >
        <div className="flex gap-x-2 items-center">
          {article.pagecategory && (
            <p className={`c1-medium py-1 px-2 ${categoryStyles.bg} ${categoryStyles.text}`}>
              {article.pagecategory}
            </p>
          )}
          {article.pagetype && (
            <p className="c1-medium text-light-fill-primary-variant uppercase">
              {article.pagetype}
            </p>
          )}
        </div>

        <p className="p2-medium">{article.name}</p>
        <ArticleCard.Subtitle className="p3-regular text-light-fill-primary-variant">
          <RichTextA11yWrapper field={{ value: article.description }} />
        </ArticleCard.Subtitle>
      </a>
    </ArticleCard.Root>
  );
};

const QueryResultsSummary = ({
  currentPage,
  itemsPerPage,
  totalItemsReturned,
  keyPhrase,
}: QueryResultsSummaryProps) => {
  return (
    <div className="font-bold my-auto mx-0 mt-4 md:mt-10">
      <p>
        You&apos;re viewing {itemsPerPage * (currentPage - 1) + 1}-
        {itemsPerPage * (currentPage - 1) + totalItemsReturned} results for &quot;{keyPhrase}
        &quot;
      </p>
    </div>
  );
};

const SearchPagination = ({ currentPage, totalPages }: SearchPaginationProps) => {
  const { onPageNumberChange } = useSearchResultsActions();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 639);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const generateSequentialPages = (start: number, end: number) => {
    const result = [];
    for (let i = start; i <= end; i++) {
      result.push(i);
    }
    return result;
  };

  const getMobilePages = () => {
    if (totalPages <= 5) {
      return generateSequentialPages(1, totalPages);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, '...', totalPages];
    }

    if (currentPage >= totalPages - 1) {
      return [1, '...', totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, '...', currentPage, '...', totalPages];
  };

  const getDesktopPages = () => {
    if (totalPages <= 7) {
      return generateSequentialPages(1, totalPages);
    }

    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }

    if (currentPage >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  // Main function with reduced complexity
  const getPages = () => {
    return isMobile ? getMobilePages() : getDesktopPages();
  };

  const pages = getPages();

  return (
    <Pagination.Root
      currentPage={currentPage}
      defaultCurrentPage={1}
      totalPages={totalPages}
      onPageChange={(v) => onPageNumberChange({ page: v })}
      className="w-full flex flex-row items-center justify-center gap-x-2 sm:gap-x-8 lg:gap-x-2 enhanced-search-pagination-wrapper"
    >
      <Pagination.PrevPage
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }}
        aria-label="Previous Page"
        className="cursor-pointer data-[current=true]:hidden group px-2 sm:px-4 py-3 "
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="21"
          viewBox="0 0 20 21"
          fill="none"
          className="rotate-180 [&_svg]:h-6 [&_svg]:w-6 [&_path]:fill-light-fill-primary-variant group-hover:[&_path]:fill-light-fill-secondary-high group-focus:[&_path]:fill-light-background-primary-highest"
        >
          <g clip-path="url(#clip0_2470_3510)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M4.82266 1.20981C4.75615 1.22104 4.69339 1.24237 4.62801 1.27599C4.57808 1.30166 4.54626 1.32657 4.48328 1.38926C4.41326 1.45897 4.39762 1.47971 4.36529 1.54563C4.31166 1.65501 4.30017 1.7053 4.30042 1.82963C4.30071 1.97308 4.32787 2.06601 4.40211 2.17763C4.41527 2.19743 6.31649 4.10453 8.62702 6.41563L12.828 10.6176L8.62702 14.8196C6.31649 17.1307 4.41527 19.0378 4.40211 19.0576C4.32787 19.1693 4.30071 19.2622 4.30042 19.4056C4.30017 19.53 4.31166 19.5803 4.36529 19.6896C4.39771 19.7558 4.41305 19.776 4.48328 19.8456C4.57397 19.9355 4.64737 19.9802 4.75201 20.0092C4.9207 20.0559 5.11654 20.027 5.26001 19.9342C5.32481 19.8923 13.7866 11.4321 13.8502 11.3456C14.1725 10.9074 14.1725 10.3278 13.8502 9.88963C13.788 9.80509 5.32485 1.34294 5.26191 1.30236C5.13707 1.22187 4.96546 1.18572 4.82266 1.20981Z"
            />
          </g>
          <defs>
            <clipPath id="clip0_2470_3510">
              <rect width="20" height="20" fill="white" transform="translate(0 0.617615)" />
            </clipPath>
          </defs>
        </svg>
      </Pagination.PrevPage>
      <Pagination.Pages className="w-full lg:w-fit flex flex-row items-center justify-between lg:justify-center gap-x-0 lg:gap-x-2 enhanced-search-pagination-pages">
        {pages.map((p, idx) => {
          if (p === '...') {
            return (
              <span key={`ellipsis-${idx}`} className="mx-2 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M2.02191 5.83698C1.49386 5.87372 1.00714 6.09565 0.633428 6.47009C0.52155 6.58218 0.450638 6.66926 0.357767 6.80862C0.175278 7.08246 0.059137 7.39658 0.0148234 7.73617C-0.00127902 7.85955 -0.00127902 8.14049 0.0148234 8.26386C0.059137 8.60346 0.175278 8.91757 0.357767 9.19142C0.450638 9.33078 0.52155 9.41786 0.633428 9.52995C1.10433 10.0018 1.74643 10.2265 2.41278 10.1528C3.34047 10.0501 4.10445 9.34999 4.29053 8.43202C4.32236 8.27497 4.32958 8.19521 4.32958 8.00002C4.32958 7.80483 4.32236 7.72507 4.29053 7.56802C4.10439 6.64976 3.34371 5.95258 2.41278 5.84705C2.30145 5.83442 2.12424 5.82986 2.02191 5.83698ZM7.86205 5.83723C7.36652 5.8689 6.89529 6.07039 6.53438 6.40493C6.28614 6.63503 6.10473 6.89931 5.98202 7.20962C5.78376 7.71097 5.78376 8.28907 5.98202 8.79042C6.13371 9.17402 6.38986 9.50675 6.71905 9.74781C6.9124 9.88939 7.1555 10.0109 7.37603 10.0762C7.84288 10.2143 8.34583 10.1937 8.79038 10.018C9.35267 9.79586 9.79583 9.3527 10.018 8.79042C10.2162 8.28869 10.2162 7.71135 10.018 7.20962C9.82152 6.71238 9.45432 6.30874 8.97918 6.06774C8.70617 5.92926 8.43808 5.85685 8.12478 5.83697C8.01249 5.82984 7.9771 5.82987 7.86205 5.83723ZM13.7312 5.83473C13.1906 5.86138 12.688 6.08336 12.3067 6.46377C11.9567 6.81309 11.7496 7.23316 11.6821 7.73122C11.6655 7.85375 11.6655 8.14628 11.6821 8.26882C11.7496 8.76634 11.957 9.18732 12.3067 9.53626C12.5499 9.77889 12.837 9.9551 13.1601 10.06C13.4798 10.1639 13.8183 10.1928 14.148 10.1444C14.6121 10.0763 15.0318 9.86458 15.3666 9.52981C15.7153 9.18111 15.9265 8.74201 15.9885 8.23682C15.9933 8.19746 15.9968 8.09936 15.9968 8.00002C15.9968 7.82776 15.9915 7.76072 15.9674 7.62242C15.9171 7.33375 15.8041 7.0517 15.6407 6.80642C15.5493 6.66928 15.478 6.58174 15.3665 6.47009C14.933 6.03572 14.3386 5.80479 13.7312 5.83473Z"
                    fill="#545454"
                  />
                </svg>
              </span>
            );
          }
          return (
            <button
              key={p}
              aria-label={p === currentPage ? `Page ${p}, Current Page` : `Go to Page ${p}`}
              className={`px-4 py-3 ${
                p === currentPage
                  ? 'c1-medium bg-light-background-neutral-variant border border-light-stroke-primary-variant-high rounded-[4px] text-light-fill-primary'
                  : 'c1 text-light-fill-primary-variant hover:bg-light-background-neutral-high focus:outline-light-stroke-primary-variant-highest'
              }`}
              onClick={(e) => {
                e.preventDefault();
                setTimeout(() => {
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }, 0);
                onPageNumberChange({ page: Number(p) });
              }}
              disabled={p === currentPage}
              aria-current={p === currentPage ? 'page' : undefined}
            >
              {p}
            </button>
          );
        })}
      </Pagination.Pages>
      <Pagination.NextPage
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }}
        aria-label="Next Page"
        className="cursor-pointer data-[current=true]:hidden group px-2 sm:px-4 py-3"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="21"
          viewBox="0 0 20 21"
          fill="none"
          className="[&_svg]:h-6 [&_svg]:w-6 [&_path]:fill-light-fill-primary-variant group-hover:[&_path]:fill-light-fill-secondary-high group-focus:[&_path]:fill-light-background-primary-highest"
        >
          <g clip-path="url(#clip0_2470_3510)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M4.82266 1.20981C4.75615 1.22104 4.69339 1.24237 4.62801 1.27599C4.57808 1.30166 4.54626 1.32657 4.48328 1.38926C4.41326 1.45897 4.39762 1.47971 4.36529 1.54563C4.31166 1.65501 4.30017 1.7053 4.30042 1.82963C4.30071 1.97308 4.32787 2.06601 4.40211 2.17763C4.41527 2.19743 6.31649 4.10453 8.62702 6.41563L12.828 10.6176L8.62702 14.8196C6.31649 17.1307 4.41527 19.0378 4.40211 19.0576C4.32787 19.1693 4.30071 19.2622 4.30042 19.4056C4.30017 19.53 4.31166 19.5803 4.36529 19.6896C4.39771 19.7558 4.41305 19.776 4.48328 19.8456C4.57397 19.9355 4.64737 19.9802 4.75201 20.0092C4.9207 20.0559 5.11654 20.027 5.26001 19.9342C5.32481 19.8923 13.7866 11.4321 13.8502 11.3456C14.1725 10.9074 14.1725 10.3278 13.8502 9.88963C13.788 9.80509 5.32485 1.34294 5.26191 1.30236C5.13707 1.22187 4.96546 1.18572 4.82266 1.20981Z"
            />
          </g>
          <defs>
            <clipPath id="clip0_2470_3510">
              <rect width="20" height="20" fill="white" transform="translate(0 0.617615)" />
            </clipPath>
          </defs>
        </svg>
      </Pagination.NextPage>
    </Pagination.Root>
  );
};

type ArticleSearchResultsProps = {
  defaultPage?: SearchResultsStoreState['page'];
  defaultItemsPerPage?: SearchResultsStoreState['itemsPerPage'];
  defaultKeyphrase?: SearchResultsStoreState['keyphrase'];
  searchBarPlaceholderText?: string;
  searchFormTarget?: string;
};

type InitialState = SearchResultsInitialState<'itemsPerPage' | 'keyphrase' | 'page'>;

const discoverSource = process.env.SEARCH_DISCOVER_URL;
const enhancedSearchSource = process.env.ENHANCED_SEARCH_SOURCE_ID?.split('|') || [];

export const EnhancedSearchComponent = ({
  defaultPage = 1,
  defaultKeyphrase = '',
  defaultItemsPerPage = 10,
  searchBarPlaceholderText,
  searchFormTarget,
}: ArticleSearchResultsProps) => {
  const {
    sanitizedResults,
    widgetRef,
    actions: { onItemClick, onKeyphraseChange, onPageNumberChange },
    state: { page, itemsPerPage = defaultItemsPerPage },
    queryResult: { isLoading, isFetching, data: { total_item: totalItems = 0 } = {} },
  } = useSanitizedSearchResults<ArticleModel, InitialState>({
    query: (query) => {
      query.getRequest().setSearchFacetAll(false).setSources(enhancedSearchSource);
    },
    state: {
      page: defaultPage,
      itemsPerPage: defaultItemsPerPage,
      keyphrase: defaultKeyphrase,
    },
  });

  const router = useRouter();
  const searchParams = useSearchParams();
  const query = searchParams.get('q') || '';
  const queryPage = Number(searchParams.get('page')) || 1;

  let searchURL: URL | null = null;
  try {
    searchURL = new URL(searchFormTarget || '');
  } catch (error) {
    console.error(error);
  }

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const [keyPhrase, setKeyPhrase] = useState<string>(query || '');
  const [searchInputValue, setSearchInputValue] = useState<string>(query || '');
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const refSearchInput = useRef<HTMLInputElement>(null);

  const fetchSuggestions = async (query: string): Promise<string[]> => {
    const response = await fetch(discoverSource || '', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add Authorization header if required by your environment
        Authorization: `Bearer ${process.env.SITECORE_SEARCH_API_KEY}`,
      },
      body: JSON.stringify({
        context: {
          locale: {
            country: 'us',
            language: 'en',
          },
        },
        widget: {
          items: [
            {
              rfk_id: 'rfkid_7',
              entity: 'content',
              sources: enhancedSearchSource, // Use your actual source ID(s)
              search: {
                suggestion: [
                  {
                    max: 4,
                    name: 'esearch_autofill',
                    keyphrase_fallback: true,
                  },
                ],
                query: {
                  keyphrase: query,
                },
              },
            },
          ],
        },
      }),
    });

    const data = await response.json();
    // Extract suggestions from the response
    type Suggestion = { text: string };
    const suggestions =
      data.widgets?.[0]?.suggestion?.esearch_autofill?.map((s: Suggestion) => s.text) || [];
    return suggestions;
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setActiveIndex(-1);

    if (value.length > 2) {
      // Fetch suggestions only if input length > 2
      const fetchedSuggestions = await fetchSuggestions(value);
      setSuggestions(fetchedSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Handle arrow keys
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0) {
        handleSuggestionClick(suggestions[activeIndex]);
        setActiveIndex(-1);
        setSuggestions([]);
      } else {
        handleSearch();
        setSuggestions([]);
        setActiveIndex(-1);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setActiveIndex(-1);
    }
  };

  const renderSuggestions = () => {
    if (suggestions.length === 0 || !searchInputValue || searchInputValue.length <= 2) return null;

    return (
      <ul
        className="suggestions-list absolute top-0 w-full bg-light-background-neutral-variant z-[5] pt-[58px] pb-4 rounded-3xl"
        style={{
          boxShadow: '0 2px 10px 0px rgba(0,0,0,0.1)',
        }}
      >
        {suggestions.map((suggestion, index) => {
          // Bold the matching part of the suggestion
          const highlightedSuggestion = highlightMatch(suggestion, searchInputValue);
          const isActive = index === activeIndex;

          return (
            <li
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              onKeyDown={(e) => {
                // Handle Enter key press
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault(); // Prevent default behavior
                  handleSuggestionClick(suggestion);
                }
              }}
              className={`suggestion-item text-p3 py-3 pl-5 outline-0 hover:bg-light-background-neutral-high hover:cursor-pointer focus-visible:bg-light-background-neutral-high ${
                isActive ? 'bg-light-background-neutral-high' : ''
              }`}
              tabIndex={0}
            >
              <span dangerouslySetInnerHTML={{ __html: highlightedSuggestion }} />
            </li>
          );
        })}
      </ul>
    );
  };

  // Function to highlight matching text with bold
  const highlightMatch = (text: string, query: string): string => {
    if (!query || query.length < 2) return text;

    // Escape special regex characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create a regex to find the query in the text (case insensitive)
    const regex = new RegExp(`(${escapedQuery})`, 'gi');

    // Replace matches with a bold version
    return text.replace(regex, '<strong>$1</strong>');
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchInputValue(suggestion);
    setSuggestions([]);
    handleSearchButton({ preventDefault: () => {} }, suggestion);
  };

  const handleSearchButton = (e: { preventDefault: () => void }, overrideValue?: string) => {
    e.preventDefault();
    setSuggestions([]);
    const valueToSearch = overrideValue ?? searchInputValue;
    router.replace(
      {
        pathname: searchURL?.pathname || '/',
        query: { q: valueToSearch },
      },
      undefined,
      { shallow: true }
    );
  };

  const handleSearch = () => {
    if (refSearchInput.current?.value === '' && searchURL?.pathname) {
      router.replace(searchURL?.pathname, undefined, {
        shallow: true,
      });
      setSuggestions([]);
      setShowSearch(false);
    } else {
      const url = {
        pathname: router.pathname,
        query: { ...router.query, q: refSearchInput.current?.value },
      };
      router.replace(url, undefined, { shallow: true });
      setSuggestions([]);
      setShowSearch(true);
    }
  };

  const handleClearSearchInput = () => {
    refSearchInput.current?.focus();
    setSearchInputValue('');
    setSuggestions([]);
  };

  const pathname = useRealPathName();

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search));
    if (page !== 0) {
      const url = {
        pathname: pathname,
        query: { ...params, page: page },
      };
      router.replace(url, undefined, { shallow: true });
    }
  }, [page]);

  useEffect(() => {
    if (query !== null) {
      setSearchInputValue(query);
      setKeyPhrase(query);
      setShowSearch(true);
      onKeyphraseChange({
        keyphrase: query,
      });
    }
  }, [query, onKeyphraseChange]);

  useEffect(() => {
    onPageNumberChange({ page: queryPage });
  }, [onPageNumberChange, queryPage]);

  useEffect(() => {
    refSearchInput.current?.focus({ preventScroll: true });
  }, [page]);

  if (!searchURL) return <></>;

  if (isLoading) {
    return (
      <div className="fixed top-0 left-0 bottom-0 right-0 flex justify-center items-center h-full w-full bg-light-background-neutral-variant bg-opacity-90">
        <Spinner loading />
      </div>
    );
  }

  return (
    <section ref={widgetRef} className="container bokf-enhanced-search-landing">
      <div className="relative block w-full max-w-[582px] mx-auto">
        <input
          ref={refSearchInput}
          onKeyDown={handleKeyDown}
          className={`z-10 relative peer border border-light-stroke-primary rounded-full w-full p-2 pl-4 pr-[94px] bg-light-background-neutral-variant focus:outline-none focus:ring-0 focus:outline-offset-[-1px] focus:outline-light-stroke-primary-variant placeholder:p3 placeholder:text-light-fill-primary-variant p3-medium`}
          style={{
            boxShadow:
              searchInputValue && searchInputValue.length >= 3
                ? 'none'
                : '0 2px 10px 0px rgba(0,0,0,0.1)',
          }}
          placeholder={searchBarPlaceholderText || 'Search...'}
          value={searchInputValue}
          autoComplete="on"
          maxLength={100}
          onChange={(e) => {
            setSearchInputValue(e.target.value);
            handleInputChange(e);
          }}
        />
        <div
          className={`absolute top-0 right-12 bottom-0 z-10 ${
            searchInputValue?.length && searchInputValue !== '' ? '' : 'hidden'
          }`}
        >
          <button
            className="flex items-center justify-center w-10 h-full"
            type="button"
            onClick={handleClearSearchInput}
            data-id="clear-search-input"
          >
            <span className="sr-only">Clear Search Input</span>
            <SVG svg="icon-close" hidden={false} className="[&_svg]:w-3 [&_svg]:h-3"></SVG>
          </button>
        </div>
        <div className="absolute top-0 right-0 bottom-0 z-10">
          <button
            className="flex items-center justify-center 
              w-[46px] h-full 
              rounded-r-full bg-light-background-gradient 
              hover:bg-light-background-gradient-hover
              focus:bg-light-background-gradient-focus  peer-focus:text-light-fill-secondary"
            type="button"
            onClick={(e) => handleSearchButton(e)}
          >
            <span className="sr-only">Search</span>
            <SVG
              svg="icon-search2"
              hidden={false}
              className="[&_svg]:h-5 [&_svg]:w-5 [&_path]:stroke-light-fill-inverse"
            ></SVG>
          </button>
        </div>
        {renderSuggestions()}
      </div>
      <div className="flex relative max-w-full px-4 text-light-fill-primary text-opacity-75">
        {isFetching && (
          <div className="w-full h-full fixed top-0 left-0 bottom-0 right-0 z-20 bg-light-background-neutral-variant opacity-90">
            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] flex flex-col justify-center items-center text-light-fill-secondary">
              <Spinner loading />
            </div>
          </div>
        )}
        {/* Show results for the last searched term, even if the input is cleared */}
        {keyPhrase && keyPhrase.trim() !== '' && totalItems > 0 && showSearch && (
          <section className="flex flex-col gap-y-2 w-full">
            <QueryResultsSummary
              currentPage={page}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              totalItemsReturned={sanitizedResults?.content?.length || defaultItemsPerPage}
              keyPhrase={keyPhrase}
              searchInputValue={searchInputValue}
            />
            <div className="flex flex-col w-full">
              {sanitizedResults?.content?.map((a, index) => (
                <ArticleHorizontalItemCard
                  key={a.id}
                  article={a}
                  index={index}
                  onItemClick={onItemClick}
                  displayText={true}
                  className=""
                />
              ))}
            </div>
            <div className="flex flex-col md:flex-row items-center justify-center mt-6 mb-16">
              <SearchPagination
                currentPage={page}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItemsReturned={totalItems}
                totalItems={totalItems}
              />
            </div>
          </section>
        )}
        {/* Only show 'no results' if search term is non-empty */}
        {searchInputValue &&
          searchInputValue.trim() !== '' &&
          totalItems <= 0 &&
          !isFetching &&
          showSearch && (
            <div className="w-full flex justify-start my-10">
              <p className="p2">
                Your search term &quot;{keyPhrase}&quot; did not return any results
              </p>
            </div>
          )}
      </div>
    </section>
  );
};
const EnhancedSearchWidget = widget(
  EnhancedSearchComponent,
  WidgetDataType.SEARCH_RESULTS,
  'content'
);
export default EnhancedSearchWidget;
